// src/admin/AdminLayout.js
import React, { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, Link, useLocation } from "react-router-dom";

export default function AdminLayout() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  // Close drawer on route change
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Mark "More" active for routes placed in the drawer
  const moreActive = useMemo(() => {
    const p = location.pathname || "";
    return (
      p.startsWith("/admin/reliability") ||
      p.startsWith("/admin/revenue") ||
      p.startsWith("/admin/users") ||
      p.startsWith("/admin/settings")
    );
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-white text-neutral-900 lg:grid lg:grid-cols-[260px_1fr]">
      {/* Desktop sidebar */}
      <aside className="hidden border-r border-neutral-200 bg-white lg:block">
        <div className="p-5">
          <Brand imageClassName="h-12 w-auto" className="mb-6" />
          <DesktopNav />
        </div>
      </aside>

      {/* Mobile header */}
      <header className="sticky top-0 z-40 flex items-center justify-between border-b border-neutral-200 bg-white/95 px-4 py-3 backdrop-blur lg:hidden">
        <button
          onClick={() => setDrawerOpen(true)}
          className="rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm active:scale-[0.99]"
          aria-label="Open menu"
          type="button"
        >
          Menu
        </button>

        <Brand imageClassName="h-10 w-auto" className="mb-0" />

        <div className="w-[64px]" aria-hidden />
      </header>

      {/* Mobile drawer */}
      <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <div className="p-5">
          <Brand imageClassName="h-12 w-auto" className="mb-5" />
          <MobileNav onNavigate={() => setDrawerOpen(false)} />
        </div>
      </MobileDrawer>

      {/* Main content */}
      <main
        className="px-4 py-4 pb-28 sm:px-6 sm:py-6 lg:pb-6"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 96px)" }}
      >
        <Outlet />
      </main>

      {/* Mobile bottom tabs */}
      <MobileTabBar
        onMore={() => setDrawerOpen(true)}
        moreActive={moreActive}
      />
    </div>
  );
}

/* ---------- Brand ---------- */
function Brand({ className = "", imageClassName = "h-10 w-auto" }) {
  return (
    <Link
      to="/"
      className={`flex items-center justify-center lg:justify-start ${className}`}
      aria-label="Go home"
    >
      <picture>
        <source srcSet="/YoureWelcome Logo.svg" type="image/svg+xml" />
        <img
          src="/YoureWelcome Logo.png"
          alt="You’re Welcome"
          className={imageClassName}
          draggable={false}
        />
      </picture>
    </Link>
  );
}

/* ---------- Desktop Nav ---------- */
function DesktopNav() {
  return (
    <nav className="grid gap-1.5 text-sm">
      <NavItem to="/admin" end icon={HomeIcon}>
        Overview
      </NavItem>

      <SectionLabel>Operations</SectionLabel>

      <NavItem to="/admin/hosts" icon={UsersIcon}>
        Hosts
      </NavItem>

      <NavItem to="/admin/listings" icon={LayersIcon}>
        Listings
      </NavItem>

      <NavItem to="/admin/verifications" icon={ShieldCheckIcon}>
        Verifications
      </NavItem>

      <NavItem to="/admin/bookings" icon={ClipboardIcon}>
        Bookings &amp; Payouts
      </NavItem>

      <SectionLabel>System</SectionLabel>

      <NavItem to="/admin/reliability" icon={AlertIcon}>
        Reliability
      </NavItem>

      <NavItem to="/admin/revenue" icon={DollarIcon}>
        Revenue
      </NavItem>

      <NavItem to="/admin/users" icon={UsersIcon}>
        Users
      </NavItem>

      <NavItem to="/admin/settings" icon={GearIcon}>
        Settings
      </NavItem>
    </nav>
  );
}

function SectionLabel({ children }) {
  return (
    <div className="mt-4 mb-1 text-[10px] font-semibold uppercase tracking-wide text-neutral-500">
      {children}
    </div>
  );
}

function NavItem({ to, end, icon: Icon, children }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        [
          "flex items-center gap-2 rounded-xl px-3 py-2.5 transition-colors outline-none",
          "focus-visible:ring-2 focus-visible:ring-black/20",
          isActive
            ? "bg-neutral-100 font-semibold text-neutral-900"
            : "text-neutral-700 hover:bg-neutral-50",
        ].join(" ")
      }
    >
      {Icon ? <Icon className="h-4 w-4" /> : null}
      <span>{children}</span>
    </NavLink>
  );
}

/* ---------- Mobile Drawer ---------- */
function MobileDrawer({ open, onClose, children }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute inset-y-0 left-0 w-[82vw] max-w-[340px] bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-neutral-200 px-4 py-3">
          <span className="text-sm font-semibold text-neutral-800">Admin</span>
          <button
            onClick={onClose}
            className="rounded-xl border border-neutral-200 bg-white px-3 py-1.5 text-xs active:scale-[0.99]"
            type="button"
          >
            Close
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function MobileNav({ onNavigate }) {
  const linkCls =
    "rounded-xl px-3 py-2.5 text-sm font-medium hover:bg-neutral-50 active:bg-neutral-100 block";

  return (
    <nav className="grid gap-1">
      <NavLink to="/admin" end className={linkCls} onClick={onNavigate}>
        Overview
      </NavLink>

      <NavLink to="/admin/hosts" className={linkCls} onClick={onNavigate}>
        Hosts
      </NavLink>

      <NavLink to="/admin/listings" className={linkCls} onClick={onNavigate}>
        Listings
      </NavLink>

      <NavLink to="/admin/verifications" className={linkCls} onClick={onNavigate}>
        Verifications
      </NavLink>

      <NavLink to="/admin/bookings" className={linkCls} onClick={onNavigate}>
        Bookings &amp; Payouts
      </NavLink>

      <div className="my-2 h-px bg-neutral-200" />

      <NavLink to="/admin/reliability" className={linkCls} onClick={onNavigate}>
        Reliability
      </NavLink>

      <NavLink to="/admin/revenue" className={linkCls} onClick={onNavigate}>
        Revenue
      </NavLink>

      <NavLink to="/admin/users" className={linkCls} onClick={onNavigate}>
        Users
      </NavLink>

      <NavLink to="/admin/settings" className={linkCls} onClick={onNavigate}>
        Settings
      </NavLink>
    </nav>
  );
}

/* ---------- Mobile Bottom Tab Bar ---------- */
function MobileTabBar({ onMore, moreActive }) {
  const Item = ({ to, label, icon: Icon, end }) => (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        [
          "flex flex-1 flex-col items-center justify-center gap-1 py-2",
          "text-[11px] leading-none",
          isActive ? "text-neutral-900" : "text-neutral-500",
        ].join(" ")
      }
    >
      <Icon className="h-5 w-5" />
      <span>{label}</span>
    </NavLink>
  );

  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-40 flex border-t border-neutral-200 bg-white/95 backdrop-blur lg:hidden"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      aria-label="Admin tabs"
    >
      <Item to="/admin" end label="Home" icon={HomeIcon} />
      <Item to="/admin/hosts" label="Hosts" icon={UsersIcon} />
      <Item to="/admin/listings" label="Listings" icon={LayersIcon} />
      <Item to="/admin/verifications" label="Verify" icon={ShieldCheckIcon} />
      <Item to="/admin/bookings" label="Bookings" icon={ClipboardIcon} />

      <button
        type="button"
        onClick={onMore}
        className={[
          "flex flex-1 flex-col items-center justify-center gap-1 py-2",
          "text-[11px] leading-none",
          moreActive ? "text-neutral-900" : "text-neutral-500",
        ].join(" ")}
        aria-label="More"
      >
        <MoreIcon className="h-5 w-5" />
        <span>More</span>
      </button>
    </nav>
  );
}

/* ---------- tiny inline icons (no deps) ---------- */
function iconBase(props) {
  return {
    ...props,
    fill: "none",
    viewBox: "0 0 24 24",
    stroke: "currentColor",
    strokeWidth: 2,
    role: "img",
    "aria-hidden": true,
  };
}

function HomeIcon(props) {
  return (
    <svg {...iconBase(props)}>
      <path d="M3 11l9-7 9 7" />
      <path d="M9 22V12h6v10" />
    </svg>
  );
}

function LayersIcon(props) {
  return (
    <svg {...iconBase(props)}>
      <path d="M12 2l9 5-9 5-9-5 9-5z" />
      <path d="M3 12l9 5 9-5" />
      <path d="M3 17l9 5 9-5" />
    </svg>
  );
}

function ShieldCheckIcon(props) {
  return (
    <svg {...iconBase(props)}>
      <path d="M12 2l7 3v6c0 5-3.5 9-7 11-3.5-2-7-6-7-11V5l7-3z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}

function ClipboardIcon(props) {
  return (
    <svg {...iconBase(props)}>
      <rect x="7" y="3" width="10" height="4" rx="1" />
      <rect x="5" y="7" width="14" height="14" rx="2" />
      <path d="M9 12h6M9 16h6" />
    </svg>
  );
}

function DollarIcon(props) {
  return (
    <svg {...iconBase(props)}>
      <path d="M12 1v22" />
      <path d="M17 5a4 4 0 0 0-4-2H9a3 3 0 0 0 0 6h6a3 3 0 0 1 0 6H9a4 4 0 0 1-4-2" />
      <path d="M7 19a4 4 0 0 0 4 2h4" />
    </svg>
  );
}

function UsersIcon(props) {
  return (
    <svg {...iconBase(props)}>
      <circle cx="9" cy="8" r="3" />
      <circle cx="17" cy="9" r="2.5" />
      <path d="M3 21a6 6 0 0 1 12 0" />
      <path d="M14 21a5 5 0 0 1 7 0" />
    </svg>
  );
}

function GearIcon(props) {
  return (
    <svg {...iconBase(props)}>
      <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V22a2 2 0 1 1-4 0v-.12a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H2a2 2 0 1 1 0-4h.12a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06A2 2 0 1 1 6.07 3.4l.06.06a1.65 1.65 0 0 0 1.82.33H8a1.65 1.65 0 0 0 1-1.51V2a2 2 0 1 1 4 0v.12a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V8c0 .66.39 1.26 1 1.51.32.14.66.2 1 .2H22a2 2 0 1 1 0 4h-.12a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

function AlertIcon(props) {
  return (
    <svg {...iconBase(props)}>
      <path d="M12 2 2 20h20L12 2z" />
      <path d="M12 9v5" />
      <circle cx="12" cy="17" r="1" />
    </svg>
  );
}

function MoreIcon(props) {
  return (
    <svg {...iconBase(props)}>
      <circle cx="5" cy="12" r="1.5" />
      <circle cx="12" cy="12" r="1.5" />
      <circle cx="19" cy="12" r="1.5" />
    </svg>
  );
}
